// Vue parity: assets/js/utils/splitStreet.js (Symfony repo) + input normalization, two-word prefixes and lieu-dit fallback
const STREET_PREFIXES = new Set([
    'abbaye',
    'agglomération',
    'aire',
    'aires',
    'allée',
    'allées',
    'ancien chemin',
    'ancienne route',
    'anciennes routes',
    'anciens chemins',
    'anse',
    'arcade',
    'arcades',
    'autoroute',
    'avenue',
    'barriere',
    'barrieres',
    'bas chemin',
    'bastide',
    'bastion',
    'beguinage',
    'béguinages',
    'berge',
    'berges',
    'bois',
    'boucle',
    'boulevard',
    'bourg',
    'butte',
    'cale',
    'camp',
    'campagne',
    'camping',
    'carre',
    'carreau',
    'carrefour',
    'carrière',
    'carrières',
    'castel',
    'cavée',
    'central',
    'centre',
    'centre commercial',
    'chalet',
    'chapelle',
    'charmille',
    'château',
    'chaussée',
    'chaussées',
    'chemin',
    'chemin vicinal',
    'cheminement',
    'cheminements',
    'chemins',
    'chemins vicinaux',
    'chez',
    'cite',
    'cites',
    'cloître',
    'clos',
    'col',
    'colline',
    'collines',
    'contour',
    'corniche',
    'corniches',
    'cote',
    'côteau',
    'cottage',
    'cottages',
    'cour',
    'cours',
    'darse',
    'degré',
    'degrés',
    'descente',
    'descentes',
    'digue',
    'digues',
    'domaine',
    'domaines',
    'écluse',
    'écluses',
    'église',
    'enceinte',
    'enclave',
    'enclos',
    'escalier',
    'escaliers',
    'espace',
    'esplanade',
    'esplanades',
    'étang',
    'faubourg',
    'ferme',
    'fermes',
    'fontaine',
    'fort',
    'forum',
    'fosse',
    'fosses',
    'foyer',
    'galerie',
    'galeries',
    'gare',
    'garenne',
    'grand boulevard',
    'grand ensemble',
    'grand rue',
    'grande rue',
    'grandes rues',
    'grands ensembles',
    'grille',
    'grimpette',
    'groupe',
    'groupement',
    'groupes',
    'halle',
    'halles',
    'hameau',
    'hameaux',
    'haut chemin',
    'hauts chemins',
    'hippodrome',
    'hlm',
    'île',
    'immeuble',
    'immeubles',
    'impasse',
    'impasses',
    'jardin',
    'jardins',
    'jetee',
    'jetees',
    'levée',
    'lieu dit',
    'lotissement',
    'lotissements',
    'mail',
    'maison forestière',
    'manoir',
    'marche',
    'marches',
    'mas',
    'métro',
    'montée',
    'montees',
    'moulin',
    'moulins',
    'musée',
    'nouvelle route',
    'palais',
    'parc',
    'parcs',
    'parking',
    'parvis',
    'passage',
    'passage à niveau',
    'passe',
    'passerelle',
    'passerelles',
    'passes',
    'patio',
    'pavillon',
    'pavillons',
    'péripherique',
    'péristyle',
    'petit chemin',
    'petite allée',
    'petite avenue',
    'petite impasse',
    'petite route',
    'petite rue',
    'petites allées',
    'place',
    'placis',
    'plage',
    'plages',
    'plaine',
    'plan',
    'plateau',
    'plateaux',
    'pointe',
    'pont',
    'ponts',
    'porche',
    'port',
    'porte',
    'portique',
    'portiques',
    'poterne',
    'pourtour',
    'pré',
    "presqu'île",
    'promenade',
    'quai',
    'quartier',
    'raccourci',
    'raidillon',
    'rampe',
    'rempart',
    'résidence',
    'résidences',
    'roc',
    'rocade',
    'rond point',
    'roquet',
    'rotonde',
    'route',
    'routes',
    'rue',
    'ruelle',
    'ruelles',
    'rues',
    'sente',
    'sentes',
    'sentier',
    'sentiers',
    'square',
    'stade',
    'station',
    'terrain',
    'terrasse',
    'terrasses',
    'terre plein',
    'tertre',
    'tertres',
    'tour',
    'traverse',
    'val',
    'vallée',
    'vallon',
    'venelle',
    'venelles',
    'via',
    'vieille route',
    'vieux chemin',
    'villa',
    'village',
    'villages',
    'villas',
    'voie',
    'voies',
    'zone',
    'zone industrielle',
    'zone à urbaniser en priorité',
    'zone artisanale',
    "zone d'activité",
    "zone d'amenagement concerté",
    "zone d'amenagement differé",
])

const NUMBER_SUFFIXES = ['bis', 'ter', 'quater', 'quarter']

function isNumberSuffix(word: string): boolean {
    const lower = word.toLowerCase()
    return NUMBER_SUFFIXES.includes(lower) || (lower.length === 1 && /[a-z]/.test(lower))
}

export interface StreetParts {
    number: string
    name: string
}

export function splitStreet(street: string | null | undefined): StreetParts {
    const parts: StreetParts = { number: '', name: '' }
    if (!street || street === 'null' || street === 'undefined') return parts

    const normalized = street.replace(/,/g, ' ').replace(/\s+/g, ' ').trim()
    if (normalized === '') return parts

    const words = normalized.split(' ')
    if (
        STREET_PREFIXES.has(words[0].toLowerCase()) ||
        STREET_PREFIXES.has(words.slice(0, 2).join(' ').toLowerCase())
    ) {
        parts.name = normalized
        return parts
    }

    // Lieu-dit fallback: without a digit there is no house number, keep the whole label as the name (Voie is mandatory for Utwin)
    if (!/\d/.test(normalized)) {
        parts.name = normalized
        return parts
    }

    let number = words.shift() as string
    if (!/^\d+$/.test(number)) {
        const digits = number.match(/[0-9]+/g)
        const letters = number.match(/[A-Za-z]+/g)
        if (digits) number = digits.join('')
        if (letters && isNumberSuffix(letters.join(''))) number = `${number} ${letters.join('')}`
    }

    if (words.length > 0 && isNumberSuffix(words[0])) {
        number = `${number} ${words.shift()}`
    }

    parts.number = number
    parts.name = words.join(' ')

    return parts
}
